import React, { MouseEvent, KeyboardEvent, useState } from 'react';
import { css } from 'emotion';
import { EpisodeAudios as EpisodeAudiosProps } from 'modules/AnchorAPI/v3/episodes/fetchEpisodeOverview';

import {
  MultiFileProgressBarContainer,
  MultiFileProgressBarStep,
} from './styles';

type MultiProgessPreviewProps = {
  episodeAudios: EpisodeAudiosProps[];
  onClick: (results: {
    localAudioPosition: number;
    activeIndex: number;
    playbackAudioPosition: number;
  }) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  activeAudioIndex: number;
  localPlaybackPosition: number;
  playableAudioDuration: number;
  playbackPosition: number;
  getGlobalPlaybackPosition: (
    audioIndex: number,
    activeAudioPlaybackPosition: number
  ) => number;
};

export const MultiFileProgressBar = ({
  episodeAudios,
  onClick,
  onKeyDown,
  activeAudioIndex,
  localPlaybackPosition,
  playbackPosition,
  playableAudioDuration,
  getGlobalPlaybackPosition,
}: MultiProgessPreviewProps) => {
  /** tracks the number of ads up until this audio point */
  const [adsMap, setAdsMap] = useState<number[]>([]);
  const [adsCount, setAdsCount] = useState<number>(0);

  /** Used to track how many ads have appeared before the current audio  */
  React.useEffect(() => {
    let ads = 0;
    setAdsMap(
      episodeAudios.reduce<number[]>((map, audio, currentIndex) => {
        const { type } = audio;
        const countAsAd = type === 'ad' ? 1 : 0;
        ads = ads + countAsAd;
        const prev = map[currentIndex - 1] ?? 0;
        map.push(prev + countAsAd);
        return map;
      }, [])
    );
    setAdsCount(ads);
  }, [episodeAudios]);

  /** Determines where we are in the progress bar */
  const getPercentComplete = ({
    isAd,
    index,
    localDuration,
  }: {
    isAd: boolean;
    index: number;
    localDuration: number;
  }) => {
    const indexWithoutAds = index - adsMap[index];

    if (
      indexWithoutAds > activeAudioIndex ||
      (isAd && indexWithoutAds === activeAudioIndex)
    )
      return 0;
    if (!isAd && indexWithoutAds === activeAudioIndex) {
      return ((localPlaybackPosition * 1000) / localDuration) * 100;
    }
    return 100;
  };

  const onProgressBarClick = (
    e: MouseEvent<HTMLElement>,
    newActiveAudioIndex: number,
    duration: number
  ) => {
    const { width, left } = e.currentTarget.getBoundingClientRect();

    const localAudioPosition = ((e.clientX - left) / width) * (duration / 1000);

    onClick({
      localAudioPosition,
      activeIndex: newActiveAudioIndex,
      playbackAudioPosition: getGlobalPlaybackPosition(
        newActiveAudioIndex,
        localAudioPosition
      ),
    });
  };

  return (
    <MultiFileProgressBarContainer
      tabIndex={0}
      onKeyDown={onKeyDown}
      role="slider"
      aria-valuenow={Math.round(
        (playbackPosition / (playableAudioDuration / 1000)) * 100
      )}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext="seek audio bar"
    >
      {episodeAudios.map((audio, index) => {
        const { duration, type, color } = audio;
        const isLastSegment = episodeAudios.length - 1 === index;
        const isAd = type === 'ad';
        const identifier = `progress-bar-${index}`;
        const progressSegmentWidth = isAd
          ? 5
          : (duration / playableAudioDuration) * (100 - adsCount * 5);

        return (
          <MultiFileProgressBarStep
            onClick={e => {
              if (!isAd) {
                const playableIndex = index - adsMap[index];
                onProgressBarClick(e, playableIndex, duration);
              }
            }}
            className={css`
              width: ${progressSegmentWidth}%;
              margin-right: ${isLastSegment ? '0' : '1px'};
              cursor: ${isAd ? 'default' : 'pointer'};
            `}
            key={identifier}
          >
            <svg width="100%" height="4px">
              <rect
                className={css`
                  opacity: 0.5;
                  pointer-events: none;
                `}
                fill={color}
                x="0"
                y="0"
                height="100%"
                width="100%"
              />
              <rect
                className={css`
                  pointer-events: none;
                `}
                fill={color}
                x="0"
                y="0"
                height="100%"
                width={`${getPercentComplete({
                  isAd,
                  index,
                  localDuration: duration,
                })}%`}
              />
            </svg>
          </MultiFileProgressBarStep>
        );
      })}
    </MultiFileProgressBarContainer>
  );
};
